/**
 * Jazz factory for a project's drawing surface.
 *
 * The document-loading hook that used to live here now sits behind the
 * `OpenProject` port (`jazz/useJazzOpenProject.ts` +
 * `jazz/JazzOpenProject.ts`). The filename is kept so the existing test
 * suites that import `createAnnotations` from here keep working.
 */
import { co, z, Group } from "jazz-tools";
import { Stroke, Annotations } from "../jazz/schema";

/**
 * The shape of a freshly-bootstrapped `Annotations` CoMap with its
 * `strokes` and `cursors` children deeply loaded. Matches the resolve query
 * the project hook uses, so values returned by `createAnnotations()` are
 * assignment-compatible with `project.$jazz.set("annotations", …)` on a
 * deeply-loaded project.
 */
type LoadedAnnotations = co.loaded<
  typeof Annotations,
  { strokes: { $each: true }; cursors: true }
>;

/**
 * Create a fresh `Annotations` CoMap with its own public-writer Group.
 *
 * Annotations live in a separate Group from the project so that read-only
 * viewers of the project (members of the project Group with role `reader`)
 * can still append strokes and cursor ticks. The annotations Group is
 * `everyone:"writer"` ("public-write"); discoverability is gated by the
 * unguessable Jazz CoValue ID, which is only reachable via the
 * (access-controlled) project document.
 *
 * Used at project create time and as a defensive lazy-create when a writer
 * opens an older project that predates the annotations field.
 */
export function createAnnotations(): LoadedAnnotations {
  const annotationsGroup = Group.create();
  annotationsGroup.makePublic("writer");

  // Strokes and cursors are non-null by construction here (we just made
  // them), but the unresolved `Annotations` shape types them as nullable.
  // Cast to the deeply-loaded shape so callers can assign directly without
  // runtime-meaningless null checks.
  return Annotations.create(
    {
      strokes: co.list(Stroke).create([], { owner: annotationsGroup }),
      cursors: co.feed(z.string()).create([], { owner: annotationsGroup }),
    },
    { owner: annotationsGroup },
  ) as LoadedAnnotations;
}
